import React, { useState } from 'react';
import API_URL from '../config';
import { showSuccess, showError } from '../utils/toast';

/**
 * EditableField — edição inline para campos diretos da NC (colunas SQL).
 *
 * Usa PUT /api/v2/nc/{id}/editar (one-shot endpoint com validator non-empty).
 * Padrão visual coerente com AnalysisFieldEditor (lápis + textarea/input).
 *
 * Props:
 *   label     — rótulo exibido em cima do valor
 *   value     — valor atual (string)
 *   campo     — nome do campo no backend (ex: 'descricao_nc', 'produto', 'lote')
 *   ncId      — id da NC
 *   multiline — true = textarea (default false → input de linha única)
 *   placeholder — placeholder do input
 *   disabled  — desabilita o botão de editar
 *   onUpdate  — callback chamado após sucesso (parent refetch)
 */
function EditableField({
  label,
  value,
  campo,
  ncId,
  multiline = false,
  placeholder = '',
  disabled = false,
  onUpdate,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft(value || '');
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(value || '');
    setIsEditing(false);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/v2/nc/${ncId}/editar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          campo,
          valor: draft,
          usuario: 'usuario@empresa.com',
        }),
      });

      if (!res.ok) {
        let detail = `Erro ${res.status}`;
        try {
          const data = await res.json();
          // Pydantic v2 retorna detail como array de objetos com .msg
          if (Array.isArray(data?.detail)) {
            detail = data.detail.map(d => d.msg || d).join('; ');
            // Limpar prefixo "Value error, " que o Pydantic v2 adiciona
            detail = detail.replace(/Value error,\s*/g, '');
          } else if (typeof data?.detail === 'string') {
            detail = data.detail;
          }
        } catch (_) {
          // resposta não-JSON
        }

        if (res.status === 422) {
          showError(detail);
        } else if (res.status >= 500) {
          showError('Erro do servidor. Tente novamente em alguns instantes.');
        } else {
          showError(detail);
        }
        return;
      }

      showSuccess('Campo atualizado.');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      // Network error ou similar
      console.error('[EditableField] Falha ao salvar', campo, err);
      showError('Sem conexão. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <div className="editable-field" style={{ position: 'relative' }}>
        {label && <label style={{ display: 'block', fontSize: '12px', color: '#57657a', marginBottom: '4px' }}>{label}</label>}
        <div style={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: '8px' }}>
          <p style={{ margin: 0, flex: 1, whiteSpace: multiline ? 'pre-wrap' : 'normal' }}>
            {value || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sem informação</span>}
          </p>
          {!disabled && (
            <button
              type="button"
              onClick={startEdit}
              title={`Editar ${label || campo}`}
              aria-label={`Editar ${label || campo}`}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px 6px',
                color: '#57657a',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>edit</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1.5px solid #006948',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  return (
    <div className="editable-field editing">
      {label && <label style={{ display: 'block', fontSize: '12px', color: '#57657a', marginBottom: '4px' }}>{label}</label>}
      {multiline ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          rows={5}
          style={{ ...inputStyle, resize: 'vertical' }}
          disabled={saving}
        />
      ) : (
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
          disabled={saving}
        />
      )}
      <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={{
            background: '#006948',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer',
          }}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          style={{
            background: 'transparent',
            color: '#57657a',
            border: '1.5px solid #d1d5db',
            borderRadius: '6px',
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default EditableField;
